import { Accordion, AccordionDetails, AccordionSummary, Divider, List, Typography } from '@mui/material';
import { Box, Stack } from '@mui/system';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { activityTypeEnum } from '../../constants';
import { getMaterialStateColor } from '../../constants/colors';
import { getTruckModeLabel } from '../../constants/labels';
import { useDeleteDailyTruckMutation, useUpdateDailyTruckMutation } from '../../hooks/mutations/useDailyTruckMutation';
import { useUpdateIncludedInExpensesMutation } from '../../hooks/mutations/useDailyTruckRideMutation';
import { useBoolean } from '../../hooks/useBoolean';
import { useSystemConfig } from '../../hooks/useSystemConfig';
import { useTheme } from '../../hooks/useTheme';
import { DataItem } from '../../services/apiSLTP/activities';
import { DailyTruckRideSiteReport } from '../../services/apiSLTP/dailyReports/activities';
import { DailyTruck } from '../../services/apiSLTP/dailyReports/dailyTrucks';
import { deleteDailyTrucksRides } from '../../services/apiSLTP/dailyReports/dailyTrucksRides';
import { capitalizeFirstLetter, formatName } from '../../utils/capitalize';
import { formatTime } from '../../utils/dates';
import { getPriceTypeConfig, materialState, systemConfigKeyEnum, truckRenterPriceTypeEnum } from '../../utils/enums';
import { formatNumber } from '../../utils/numbers';
import LicensePlate from '../LicensePlate';
import ManageValidationDailyTruckModal from '../modals/operations/validation/ManageDailyTruckModal';
import ManageValidationDailyTruckRideModal from '../modals/operations/validation/ManageNewDailyTruckRideModal';
import QuantityInput from '../QuantityInput';
import QuantityToggleGroup from '../QuantityToggleGroup';
import Icon from './Icon';
import IconButton from './IconButton';
import LabelValue from './LabelValue';
import ReportItemCard from './ReportItemCard';
import SectionCard from './SectionCard';
import ManageValidationDailyTruckPricesModal from '../modals/operations/validation/ManageDailyTruckPricesModal';
import { isTruckContextEnabled } from '../../constants/helpers/truck';

interface DailyTrucksSectionProps {
    dailyTrucks: DailyTruck[];
    rides: DailyTruckRideSiteReport[];
    title: string;
    readonly?: boolean;
    siteName?: string;
    activities?: DataItem[];
    currentDate: Date;
    refetch?: () => void;
    disabled?: boolean;
    isOwnCompany: boolean;
}

const DailyTrucksSection: React.FC<DailyTrucksSectionProps> = ({
    dailyTrucks,
    rides,
    title,
    readonly,
    activities,
    currentDate,
    refetch = () => {},
    siteName,
    disabled = false,
    isOwnCompany,
}) => {
    const { t } = useTranslation();
    const { muiTheme } = useTheme();
    const { getConfigValue } = useSystemConfig();

    const dailyTruckRideModal = useBoolean(false);
    const [selectedDailyTruckRide, setSelectedDailyTruckRide] = React.useState<DailyTruckRideSiteReport | null>(null);
    const [selectedInitTruck, setSelectedInitTruck] = React.useState<string | null>(null);
    const dailyTruckModal = useBoolean(false);
    const dailyTruckPricesModal = useBoolean(false);
    const [selectedDailyTruck, setSelectedDailyTruck] = React.useState<DailyTruck | null>(null);

    const { mutate: mutateIncludedInExpenses, isLoading: isLoadingIncludedInExpenses } = useUpdateIncludedInExpensesMutation({
        onSuccess: () => {
            refetch();
        },
    });

    const { mutate: updateDailyTruck } = useUpdateDailyTruckMutation({
        onSuccess: () => {
            refetch();
        },
    });
    const { mutate: deleteDailyTruck } = useDeleteDailyTruckMutation({
        onSuccess: () => {
            refetch();
        },
    });

    const dTrucks = useMemo(() => {
        return dailyTrucks
            .filter((dt) => !!dt.truckId.truckCompanyId.isOwnCompany === isOwnCompany)
            .sort((a, b) => (a.truckId.truckTypeId.displayOrder || Infinity) - (b.truckId.truckTypeId.displayOrder || Infinity));
    }, [dailyTrucks]);

    const groupAndSortByModeAndNature = (dataItems: DailyTruckRideSiteReport[]): DailyTruckRideSiteReport[][] => {
        const groupedByRidesInfos: { [key: string]: DailyTruckRideSiteReport[] } = {};
        dataItems.forEach((item) => {
            const truckModeEnum = (item.mode ?? -1).toString();
            const truckNature = (item.departureLoadNatureId?._id || '') + (item.arrivalLoadNatureId?._id || '');
            const truckSite = (item.departureSiteId?._id || '') + (item.arrivalSiteId?._id || '');
            const truckQuarry = (item.departureQuarryId?._id || '') + (item.arrivalQuarryId?._id || '');
            const truckDump = (item.departureDumpId?._id || '') + (item.arrivalDumpId?._id || '');
            let truckTransfers = '';
            for (const transfer of item.transfers) {
                truckTransfers += transfer._id;
            }

            const sortString = truckNature + truckModeEnum + truckSite + truckQuarry + truckDump + truckTransfers;

            if (!groupedByRidesInfos[sortString]) {
                groupedByRidesInfos[sortString] = [];
            }
            groupedByRidesInfos[sortString].push(item);
        });

        const groupedTrucks: DailyTruckRideSiteReport[][] = Object.values(groupedByRidesInfos);
        // ADD SORT FIRST RIDE TIME
        groupedTrucks.sort((a, b) => {
            const modeA = a[0].mode ?? -1;
            const modeB = b[0].mode ?? -1;
            return modeA - modeB;
        });
        return groupedTrucks;
    };

    useEffect(() => {
        if (selectedDailyTruckRide?._id && rides.length) {
            const found = rides.find((ride) => ride._id === selectedDailyTruckRide._id);
            setSelectedDailyTruckRide(found || null);
        }
    }, [rides]);

    useEffect(() => {
        if (!dailyTruckModal.value || !selectedDailyTruck) return;

        const freshTruck = dailyTrucks.find((t) => t._id === selectedDailyTruck._id);
        if (freshTruck) setSelectedDailyTruck(freshTruck);
    }, [dailyTrucks, dailyTruckModal.value]);

    return (
        <SectionCard
            title={title}
            disabled={readonly || disabled}
            onAdd={() => {
                dailyTruckModal.setTrue();
                setSelectedDailyTruck(null);
            }}
            iconTitleName={isOwnCompany ? 'localShipping' : 'localShipping'}
            totalPrice={
                !!dTrucks.length
                    ? dTrucks.reduce((acc, truck) => {
                          const truckRides = rides.filter((ride) => ride.truckId._id === truck.truckId._id);
                          return (
                              acc +
                              (isOwnCompany
                                  ? truck.quantity * (truck.unitPrice ?? truck.truckId.price ?? truck.truckId.truckTypeId.price)
                                  : truck.unitPrice
                                    ? truck.quantity * truck.unitPrice
                                    : 0)
                          );
                      }, 0)
                    : undefined
            }
            extraActions={
                <>
                    {!isOwnCompany ? (
                        <IconButton
                            iconName="priceChange"
                            onClick={() => {
                                dailyTruckPricesModal.setTrue();
                            }}
                            disabled={!dTrucks.length}
                        />
                    ) : null}
                </>
            }
        >
            {dTrucks.map((truck, index) => {
                const truckRides = rides.filter((ride) => ride.truckId._id === truck.truckId._id);
                const { step, min, max, decimals } = getPriceTypeConfig(truck.priceType || -1);

                return (
                    <ReportItemCard
                        key={truck._id}
                        title={
                            isOwnCompany
                                ? truck.truckId.designation
                                : `${truck.truckId.truckCompanyId.designation} ${truck.truckId.truckTypeId.designation}`
                        }
                        titleComponent={<LicensePlate number={truck.truckId.plateNumber} />}
                        disabledOnDelete={!!truckRides.length}
                        disabled={disabled}
                        extraActions={
                            <IconButton
                                iconName="addRoad"
                                onClick={() => {
                                    dailyTruckRideModal.setTrue();
                                    setSelectedDailyTruckRide(null);
                                    setSelectedInitTruck(truck.truckId._id);
                                }}
                            />
                        }
                        majorAction={
                            <>
                                {truck.priceType === truckRenterPriceTypeEnum.DAY ? (
                                    <QuantityToggleGroup
                                        value={truck.quantity}
                                        onChange={(value) => updateDailyTruck({ id: truck._id, body: { quantity: value } })}
                                        disabled={disabled}
                                    />
                                ) : truck.priceType === truckRenterPriceTypeEnum.UNIT || truck.priceType === truckRenterPriceTypeEnum.TON ? (
                                    <QuantityInput
                                        value={truck.quantity}
                                        step={step}
                                        min={min}
                                        max={max}
                                        decimals={decimals}
                                        unit={truck.priceType === truckRenterPriceTypeEnum.UNIT ? capitalizeFirstLetter(t('ride(s)')) : 'T'}
                                        onChange={(value) => updateDailyTruck({ id: truck._id, body: { quantity: value } })}
                                        disabled={disabled}
                                    />
                                ) : null}
                            </>
                        }
                        onEdit={() => {
                            setSelectedDailyTruck(truck);
                            dailyTruckModal.setTrue();
                        }}
                        onDelete={() => deleteDailyTruck({ id: truck._id })}
                        price={
                            isOwnCompany
                                ? truck.quantity * (truck.unitPrice ?? truck.truckId.price ?? truck.truckId.truckTypeId.price)
                                : truck.unitPrice
                                  ? truck.quantity * truck.unitPrice
                                  : undefined
                        }
                        extraInfo={
                            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center' }}>
                                {isOwnCompany && (
                                    <>
                                        <Box sx={{ minWidth: '75px', justifyContent: 'flex-end', display: 'flex' }}>
                                            {truckRides.some((ride) => ride.isDecommissioned) && (
                                                <Typography variant="body2" fontWeight={600} sx={{ color: muiTheme.palette.error.main }}>
                                                    {truckRides.reduce((sum, ride) => sum + (ride.isDecommissioned ? 1 : 0), 0)} {t('decommissioned')}
                                                </Typography>
                                            )}
                                        </Box>
                                        <Box sx={{ minWidth: '75px', justifyContent: 'flex-end', display: 'flex' }}>
                                            {truckRides.some((ride) => ride.departureTonnage && ride.departureTonnage > 0) && (
                                                <Typography variant="body2" fontWeight={600} sx={{ color: '#00b8c2' }}>
                                                    {formatNumber(
                                                        truckRides.reduce((sum, ride) => sum + (ride.departureTonnage || 0), 0),
                                                        t('locale')
                                                    )}
                                                    T {t('appro')}
                                                </Typography>
                                            )}
                                        </Box>
                                        <Box sx={{ minWidth: '75px', justifyContent: 'flex-end', display: 'flex' }}>
                                            {truckRides.some((ride) => ride.arrivalTonnage && ride.arrivalTonnage > 0) && (
                                                <Typography variant="body2" fontWeight={600} sx={{ color: 'lightgreen' }}>
                                                    {formatNumber(
                                                        truckRides.reduce((sum, ride) => sum + (ride.arrivalTonnage || 0), 0),
                                                        t('locale')
                                                    )}
                                                    T {t('evac')}
                                                </Typography>
                                            )}
                                        </Box>
                                    </>
                                )}
                                <Box sx={{ minWidth: '75px', justifyContent: 'flex-end', display: 'flex' }}>
                                    <Typography variant="body2" fontWeight={600}>
                                        {truckRides.length} {t('ride(s)')}
                                    </Typography>
                                </Box>
                            </Box>
                        }
                    >
                        <LabelValue
                            title={capitalizeFirstLetter(t('activityOf'))}
                            value={formatName(
                                activities?.find((activity) => activity._id === truck.activityId)?.reportId.userId.firstName || '',
                                activities?.find((activity) => activity._id === truck.activityId)?.reportId.userId.lastName || ''
                            )}
                        />
                        {isOwnCompany ? (
                            <LabelValue
                                title={capitalizeFirstLetter(t('unitPrice'))}
                                value={`${truck.unitPrice ?? truck.truckId.price ?? truck.truckId.truckTypeId.price} ${getConfigValue(systemConfigKeyEnum.CURRENCY_SYMBOL)}`}
                            />
                        ) : typeof truck.unitPrice === 'number' ? (
                            <LabelValue
                                title={capitalizeFirstLetter(t('unitPrice'))}
                                value={`${truck.unitPrice} ${getConfigValue(systemConfigKeyEnum.CURRENCY_SYMBOL)}`}
                            />
                        ) : null}
                        {!!truck.truckId.codeProC && <LabelValue title={capitalizeFirstLetter(t('codeProC'))} value={truck.truckId.codeProC} />}
                        {!!groupAndSortByModeAndNature(truckRides).length
                            ? groupAndSortByModeAndNature(truckRides).map((modeRides, index) => (
                                  <Accordion
                                      key={modeRides[0]._id + index}
                                      square
                                      sx={{
                                          backgroundColor: muiTheme.palette.background.default,
                                          boxShadow: 'none',
                                      }}
                                  >
                                      <AccordionSummary
                                          expandIcon={<Icon name="keyboardArrowRightSharp" />}
                                          sx={{
                                              flexDirection: 'row-reverse',
                                              backgroundColor: muiTheme.palette.background.default,
                                              boxShadow: 'none',
                                              '&.Mui-expanded .MuiAccordionSummary-expandIconWrapper': {
                                                  transform: 'rotate(90deg)',
                                              },
                                          }}
                                      >
                                          <Box
                                              sx={{
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  gap: 1,
                                                  flexDirection: 'row',
                                                  justifyContent: 'space-between',
                                                  flexGrow: 1,
                                                  pl: 2,
                                              }}
                                          >
                                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexDirection: 'row', flex: 1 }}>
                                                  <Typography variant="subtitle2">
                                                      {`${modeRides.length} ${capitalizeFirstLetter(t(getTruckModeLabel(modeRides[0].mode)))} `}
                                                      {isTruckContextEnabled(modeRides[0].mode, 'departure') &&
                                                          ` ${modeRides[0].departureDumpId?.designation ? ` ${capitalizeFirstLetter(t('dump'))} ${modeRides[0].departureDumpId?.designation.toLowerCase()}` : ''} ${modeRides[0].departureLoadNatureId?.designation ? ` ${modeRides[0].departureLoadNatureId?.designation}` : ''} ${modeRides[0].departureSiteId?.name || modeRides[0].departureQuarryId?.designation ? ` ${t('from')} ${modeRides[0].departureSiteId?.name || modeRides[0].departureQuarryId?.designation}` : ''}`}
                                                      {isTruckContextEnabled(modeRides[0].mode, 'arrival') &&
                                                          ` ${modeRides[0].arrivalDumpId?.designation ? ` ${capitalizeFirstLetter(t('dump'))} ${modeRides[0].arrivalDumpId?.designation.toLowerCase()}` : ''} ${modeRides[0].arrivalLoadNatureId?.designation ? ` ${modeRides[0].arrivalLoadNatureId?.designation}` : ''} ${modeRides[0].arrivalSiteId?.name || modeRides[0].arrivalQuarryId?.designation ? ` ${t('to')} ${modeRides[0].arrivalSiteId?.name || modeRides[0].arrivalQuarryId?.designation}` : ''}`}
                                                  </Typography>
                                                  <Box
                                                      sx={{
                                                          display: 'flex',
                                                          alignItems: 'center',
                                                          gap: 1,
                                                          flexDirection: 'row',
                                                          flex: 1,
                                                          flexWrap: 'wrap',
                                                      }}
                                                  >
                                                      {modeRides[0].transfers.map((transfer) => (
                                                          <Box
                                                              key={transfer._id}
                                                              sx={{
                                                                  display: 'flex',
                                                                  flexDirection: 'column',
                                                                  borderRadius: 1,
                                                                  bgcolor: getMaterialStateColor(transfer.state, 'background'),
                                                                  color: getMaterialStateColor(transfer.state, 'text'),
                                                                  px: 1,
                                                              }}
                                                          >
                                                              <Typography>
                                                                  {transfer.machineId?.designation ||
                                                                      transfer.toolId?.designation ||
                                                                      transfer.rentalName + ' ' + transfer.rentalCompanyId?.designation}
                                                              </Typography>
                                                              <Typography>
                                                                  {transfer.state === materialState.NEW ? t('from') : t('to')}{' '}
                                                                  {transfer.siteId?.name || transfer.quarryId?.designation}
                                                              </Typography>
                                                          </Box>
                                                      ))}
                                                  </Box>
                                              </Box>
                                              {isOwnCompany && (
                                                  <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'row' }}>
                                                      <Box sx={{ minWidth: '75px', justifyContent: 'flex-end', display: 'flex' }}>
                                                          {modeRides.some((ride) => ride.isDecommissioned) && (
                                                              <Typography variant="subtitle2" sx={{ color: muiTheme.palette.error.main }}>
                                                                  {modeRides.reduce((sum, ride) => sum + (ride.isDecommissioned ? 1 : 0), 0)}
                                                                  {' ' + t('decommissioned')}
                                                              </Typography>
                                                          )}
                                                      </Box>
                                                      <Box sx={{ minWidth: '75px', justifyContent: 'flex-end', display: 'flex' }}>
                                                          {modeRides.some((ride) => ride.departureTonnage && ride.departureTonnage > 0) && (
                                                              <Typography variant="subtitle2" sx={{ color: '#00b8c2' }}>
                                                                  {formatNumber(
                                                                      modeRides.reduce((sum, ride) => sum + (ride.departureTonnage || 0), 0),
                                                                      t('locale')
                                                                  )}
                                                                  T
                                                              </Typography>
                                                          )}
                                                      </Box>
                                                      <Box sx={{ minWidth: '75px', justifyContent: 'flex-end', display: 'flex' }}>
                                                          {modeRides.some((ride) => ride.arrivalTonnage && ride.arrivalTonnage > 0) && (
                                                              <Typography variant="subtitle2" sx={{ color: 'lightgreen' }}>
                                                                  {formatNumber(
                                                                      modeRides.reduce((sum, ride) => sum + (ride.arrivalTonnage || 0), 0),
                                                                      t('locale')
                                                                  )}
                                                                  T
                                                              </Typography>
                                                          )}
                                                      </Box>
                                                  </Box>
                                              )}
                                          </Box>
                                      </AccordionSummary>
                                      <AccordionDetails sx={{ backgroundColor: muiTheme.palette.background.default, boxShadow: 'none' }}>
                                          <List>
                                              {modeRides
                                                  .sort((a, b) => {
                                                      if (!a.time && !b.time) return 0;
                                                      if (!a.time) return 1;
                                                      if (!b.time) return -1;

                                                      return new Date(a.time).getTime() - new Date(b.time).getTime();
                                                  })
                                                  .map((ride) => (
                                                      <Box key={ride._id}>
                                                          <Box
                                                              sx={{
                                                                  display: 'flex',
                                                                  alignItems: 'center',
                                                                  gap: 2,
                                                                  flexDirection: 'row',
                                                                  justifyContent: 'space-between',
                                                                  p: 1,
                                                                  borderLeft: ride.isDecommissioned ? `6px solid ${muiTheme.palette.error.main}` : '',
                                                              }}
                                                          >
                                                              <Box>
                                                                  {isTruckContextEnabled(modeRides[0].mode, 'departure') && (
                                                                      <Stack direction="row" spacing={1}>
                                                                          <Box sx={{ width: '50px' }}>
                                                                              {isOwnCompany
                                                                                  ? ride.departureTime
                                                                                      ? formatTime(new Date(ride.departureTime))
                                                                                      : '?'
                                                                                  : ''}
                                                                          </Box>
                                                                          {isOwnCompany && (
                                                                              <Box
                                                                                  sx={{
                                                                                      minWidth: '50px',
                                                                                      color: '#00b8c2',
                                                                                      display: 'flex',
                                                                                      justifyContent: 'flex-end',
                                                                                  }}
                                                                              >
                                                                                  {ride.departureTonnage
                                                                                      ? `${formatNumber(ride.departureTonnage, t('locale'))}T`
                                                                                      : ''}
                                                                              </Box>
                                                                          )}
                                                                          <Box>
                                                                              {ride.departureSiteId?.name || ride.departureQuarryId?.designation}
                                                                          </Box>
                                                                      </Stack>
                                                                  )}
                                                                  <Stack direction="row" spacing={1}>
                                                                      <Box sx={{ width: '50px' }}>
                                                                          {ride.time ? formatTime(new Date(ride.time)) : '?'}
                                                                      </Box>
                                                                      {isOwnCompany && <Box sx={{ minWidth: '50px' }} />}
                                                                      <Box>{ride.site?.name || ''}</Box>
                                                                  </Stack>
                                                                  {isTruckContextEnabled(modeRides[0].mode, 'arrival') && (
                                                                      <Stack direction="row" spacing={1}>
                                                                          <Box sx={{ width: '50px' }}>
                                                                              {isOwnCompany
                                                                                  ? ride.arrivalTime
                                                                                      ? formatTime(new Date(ride.arrivalTime))
                                                                                      : '?'
                                                                                  : ''}
                                                                          </Box>
                                                                          {isOwnCompany && (
                                                                              <Box
                                                                                  sx={{
                                                                                      minWidth: '50px',
                                                                                      color: 'lightgreen',
                                                                                      display: 'flex',
                                                                                      justifyContent: 'flex-end',
                                                                                  }}
                                                                              >
                                                                                  {ride.arrivalTonnage
                                                                                      ? `${formatNumber(ride.arrivalTonnage, t('locale'))}T`
                                                                                      : ''}
                                                                              </Box>
                                                                          )}
                                                                          <Box>{ride.arrivalSiteId?.name || ride.arrivalQuarryId?.designation}</Box>
                                                                      </Stack>
                                                                  )}
                                                                  {isTruckContextEnabled(modeRides[0].mode, 'secondTimeOnSite') && (
                                                                      <Stack direction="row" spacing={1}>
                                                                          <Box sx={{ width: '50px' }}>
                                                                              {isOwnCompany
                                                                                  ? ride.secondTime
                                                                                      ? formatTime(new Date(ride.secondTime))
                                                                                      : '?'
                                                                                  : ''}
                                                                          </Box>
                                                                          {isOwnCompany && <Box sx={{ minWidth: '50px' }} />}
                                                                          <Box>{ride.site?.name || ''}</Box>
                                                                      </Stack>
                                                                  )}
                                                                  {ride.observations}
                                                              </Box>

                                                              {!readonly && !disabled && (
                                                                  <Box>
                                                                      <IconButton
                                                                          iconName="edit"
                                                                          onClick={() => {
                                                                              dailyTruckRideModal.setTrue();
                                                                              setSelectedDailyTruckRide(ride);
                                                                              setSelectedInitTruck(null);
                                                                          }}
                                                                      />
                                                                      <IconButton
                                                                          iconName="deleteForever"
                                                                          color="error"
                                                                          onClick={() => {
                                                                              if (window.confirm(capitalizeFirstLetter(t('confirmDelete')))) {
                                                                                  deleteDailyTrucksRides(ride._id);
                                                                              }
                                                                          }}
                                                                      />
                                                                      {/*truck.priceType === truckPriceTypeEnum.UNIT && (
                                                                          <Checkbox
                                                                              checked={ride.includedInExpenses}
                                                                              disabled={readonly || disabled}
                                                                              title={capitalizeFirstLetter(t('includedInExpenses'))}
                                                                              color="success"
                                                                              onChange={(event) => {
                                                                                  const checked = event.target.checked;
                                                                                  mutateIncludedInExpenses({
                                                                                      ids: [ride._id],
                                                                                      includedInExpenses: checked,
                                                                                  });
                                                                              }}
                                                                          />
                                                                      )*/}
                                                                  </Box>
                                                              )}
                                                          </Box>
                                                          <Divider />
                                                      </Box>
                                                  ))}
                                          </List>
                                      </AccordionDetails>
                                  </Accordion>
                              ))
                            : null}
                    </ReportItemCard>
                );
            })}
            {dailyTruckRideModal.value && (
                <ManageValidationDailyTruckRideModal
                    open={dailyTruckRideModal.value}
                    handleClose={dailyTruckRideModal.setFalse}
                    dailyTruckRide={selectedDailyTruckRide}
                    activities={
                        activities
                            ?.filter((activity) =>
                                !isOwnCompany
                                    ? activity.activityTypeId.type !== activityTypeEnum.TRUCK
                                    : activity.activityTypeId.type === activityTypeEnum.TRUCK
                            )
                            .map((activity) => ({
                                label: `${formatName(activity.reportId.userId.firstName, activity.reportId.userId.lastName)} - ${capitalizeFirstLetter(!isOwnCompany ? t('truckManager') : t(activity.activityTypeId.designation))}`,
                                value: activity._id,
                            })) || []
                    }
                    refetch={refetch}
                    siteName={siteName}
                    currentDate={currentDate}
                    initTruckId={selectedInitTruck}
                />
            )}
            {dailyTruckModal.value && (
                <ManageValidationDailyTruckModal
                    open={dailyTruckModal.value}
                    handleClose={dailyTruckModal.setFalse}
                    dailyTruck={selectedDailyTruck}
                    activities={
                        activities
                            ?.filter((activity) =>
                                !isOwnCompany
                                    ? activity.activityTypeId.type !== activityTypeEnum.TRUCK
                                    : activity.activityTypeId.type === activityTypeEnum.TRUCK
                            )
                            .map((activity) => ({
                                label: `${formatName(activity.reportId.userId.firstName, activity.reportId.userId.lastName)} - ${capitalizeFirstLetter(!isOwnCompany ? t('truckManager') : t(activity.activityTypeId.designation))}`,
                                value: activity._id,
                            })) || []
                    }
                    refetch={refetch}
                    siteName={siteName}
                    isOwnCompany={isOwnCompany}
                />
            )}
            {dailyTruckPricesModal.value && (
                <ManageValidationDailyTruckPricesModal
                    open={dailyTruckPricesModal.value}
                    handleClose={dailyTruckPricesModal.setFalse}
                    dailyTrucks={dailyTrucks}
                    refetch={refetch}
                    siteName={siteName}
                />
            )}
        </SectionCard>
    );
};

export default DailyTrucksSection;
